import type { CityConfig } from "../city-template/types";

export const cantonConfig: CityConfig = {
  city: "Canton",
  state: "Michigan",
  stateAbbr: "MI",
  slug: "canton-mi",
  route: "/cities/canton-mi",
  title: "Emergency Passport Canton MI | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Canton, Michigan — as fast as 24 hours. Fast passport renewal and new passport applications for Canton, Plymouth, Northville, Westland, and all western Detroit metro residents. A+ BBB rated courier service.",
  h1: "Emergency & Expedited Passport Services in Canton, MI",
  heroSubtitle: "Canton travelers — from Ford Road commuters to Plymouth residents to Northville families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. Western Detroit suburbs covered with no need to drive downtown. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20aerial%20view%20of%20Canton%20Michigan%20suburban%20landscape%20with%20tree-lined%20residential%20streets%2C%20modern%20shopping%20centers%20along%20Ford%20Road%2C%20green%20parks%20and%20open%20spaces%2C%20suburban%20Detroit%20western%20metro%20area%2C%20soft%20afternoon%20light%2C%20clean%20well-maintained%20neighborhoods%2C%20no%20text%20no%20people%2C%20professional%20suburban%20photography%2C%20calm%20and%20peaceful%20atmosphere&width=1800&height=700&seq=canton-mi-hero-001&orientation=landscape",
  heroImageAlt: "Canton Michigan suburban landscape with Ford Road corridor and tree-lined neighborhoods",
  geoPosition: "42.3086;-83.4821",
  geoPlacename: "Canton, Michigan",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Detroit Passport Agency",
    address: "211 W. Fort St, Suite 1300, Detroit, MI 48226",
    distance: "Approximately 25 miles east of Canton — about 35 minutes via I-96",
  },
  nearbyCitiesZips: [
    { name: "Canton", zips: "48187, 48188" },
    { name: "Plymouth", zips: "48170" },
    { name: "Northville", zips: "48167, 48168" },
    { name: "Westland", zips: "48185, 48186" },
    { name: "Livonia", zips: "48150, 48151, 48152, 48153, 48154" },
    { name: "Garden City", zips: "48135, 48136" },
    { name: "Inkster", zips: "48141" },
    { name: "Wayne", zips: "48184" },
    { name: "Romulus", zips: "48174" },
    { name: "Belleville", zips: "48111, 48112" },
    { name: "Ypsilanti", zips: "48197, 48198" },
    { name: "Ann Arbor", zips: "48103, 48104, 48105, 48108, 48109" },
    { name: "Novi", zips: "48374, 48375, 48377" },
    { name: "Farmington Hills", zips: "48331, 48333, 48334, 48335, 48336" },
    { name: "Walled Lake", zips: "48390" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to the Detroit Passport Agency — no appointment wait" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-truck-line", title: "No Driving to Downtown", desc: "Ship from Canton — our couriers handle the agency submission" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — stay home while we do the work" },
  ],
  internalLinks: [
    { name: "Expedited passport in Detroit, MI", route: "/cities/detroit" },
    { name: "Emergency passport services in Ann Arbor, MI", route: "/cities/ann-arbor" },
    { name: "Urgent passport processing in Livonia, MI", route: "/states/michigan" },
    { name: "Michigan passport services overview", route: "/states/michigan" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Canton, Michigan via secure registered courier and has direct access to the Detroit Passport Agency at 211 W. Fort St, Suite 1300. Our registered couriers hand-deliver your documents — meaning you get faster results without the downtown drive. Canton residents can ship from any UPS or FedEx in the western Detroit metro.",
  fpcOfficeDistance: "Serving Canton, Plymouth, Northville, Westland, Livonia, Garden City, Inkster, Wayne, Romulus, and all western Detroit suburbs",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Canton and the western Detroit metro area. From the Canton Village Theater to the Plymouth Historic District, from the Northville downtown to the Westland shopping centers — we serve all of Wayne and western Oakland counties. Our courier service is especially convenient for Canton residents who would otherwise need to drive downtown Detroit.",
  nearbyCitiesIntro: "We serve Canton and all of western Detroit metro including Plymouth, Northville, Westland, Livonia, Garden City, Inkster, Wayne, Romulus, and Belleville. We also serve neighboring Washtenaw County (Ann Arbor, Ypsilanti) and Oakland County (Novi, Farmington Hills, Walled Lake).",
  howItWorksIntro: "Most Canton travelers complete everything online and ship documents overnight. No driving downtown to the Detroit Passport Agency — our registered couriers handle the agency submission for you.",
  mistakesIntro: "Canton is a fast-growing suburb with a diverse international community. Here are the most common passport mistakes we see from western Detroit metro travelers.",
  testimonialsIntro: "Canton travelers — from automotive professionals to international families to students — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Canton, MI? Here are the most common ones we hear from Canton and western Detroit metro travelers.",
  statePageRoute: "/states/michigan",
  statePageName: "Michigan",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Detroit Passport Agency (Self-Service)",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — typically 2 weeks out", "Life-or-death emergencies only", "Must drive to downtown Detroit — 25 miles", "Full day commitment — parking + wait time"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "No driving downtown — ship from Canton", "No appointment wait — we handle submission", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Direct access to Detroit Passport Agency", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Canton travelers with immediate departure needs — as fast as 24 hours. Our couriers go directly to the Detroit Passport Agency. Perfect for last-minute business trips, family emergencies, and international travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier to Detroit Passport Agency", "No need to drive downtown to the agency"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Canton adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Canton address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Canton residents and families — including students, new citizens, and families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Canton residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Canton-area families planning international vacations or visiting family abroad.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Canton residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Canton MI", description: "Urgent passport processing for Canton Michigan travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Canton MI", description: "Fast passport renewal for Canton Michigan residents with a previously-issued passport." },
    { name: "First-Time Passport Canton MI", description: "New passport application services for Canton Michigan residents applying for their first passport." },
    { name: "Child Passport Canton MI", description: "Minor passport services for Canton Michigan families with children under 16." },
  ],
  testimonials: [
    {
      name: "Rachel S.",
      location: "Canton, MI",
      rating: 5,
      text: "Business trip to Germany in 4 days and my passport was expired. Fast Passport Center handled everything — delivered my renewed passport in 36 hours. I never left Canton. Their courier handled the Detroit agency visit while I was at work.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Ahmed H.",
      location: "Plymouth, MI",
      rating: 5,
      text: "Family trip to India with 9 days notice. Four passports for our whole family. Done in 5 days. We shipped from the FedEx on Ford Road and everything went smooth. Professional service.",
      service: "Family Passport Bundle",
    },
    {
      name: "Jessica T.",
      location: "Northville, MI",
      rating: 5,
      text: "Conference in London and I realized my passport was expiring too soon. Fast Passport Center got me a renewed passport in 2 days. The process was completely hands-off from my end.",
      service: "Passport Renewal",
    },
    {
      name: "David L.",
      location: "Westland, MI",
      rating: 5,
      text: "Needed to cross to Canada for a family emergency and lost my passport the day before. Fast Passport Center walked me through the process and had a replacement in my hands in 48 hours.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "canton-1",
      name: "Canton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "435 S Canton Center Rd",
      city: "Canton",
      state: "MI",
      zip: "48188",
      phone: "(734) 397-4200",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Canton — near Canton Center and Ford Road",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Canton acceptance facility. Ample parking. Serves Canton, Plymouth, and western Wayne County residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-2",
      name: "Plymouth Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "475 S Main St",
      city: "Plymouth",
      state: "MI",
      zip: "48170",
      phone: "(734) 453-7000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Plymouth — approximately 5 miles north of Canton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Plymouth and northern Canton residents. Located in the historic downtown Plymouth district. Book early — high demand during summer.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-3",
      name: "Northville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "123 W Main St",
      city: "Northville",
      state: "MI",
      zip: "48167",
      phone: "(248) 349-8000",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Northville — approximately 8 miles northeast of Canton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Northville and eastern Canton residents. Convenient to downtown Northville. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-4",
      name: "Westland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37245 Ford Rd",
      city: "Westland",
      state: "MI",
      zip: "48185",
      phone: "(734) 728-1800",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Westland — approximately 6 miles southeast of Canton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Westland, Garden City, and southern Canton residents. Located on Ford Road. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-5",
      name: "Detroit Passport Agency",
      type: "Regional Passport Agency",
      address: "211 W. Fort St, Suite 1300",
      city: "Detroit",
      state: "MI",
      zip: "48226",
      phone: "(313) 226-7110",
      hours: "Mon–Fri: 8:00 AM – 3:00 PM (by appointment only)",
      distance: "Approximately 25 miles east of Canton — about 35 minutes via I-96",
      website: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html",
      websiteLabel: "Schedule Appointment",
      notes: "Michigan's only regional passport agency. Serves travelers with international travel within 14 days (or 28 days with visa). Appointments are required — no walk-ins. Fast Passport Center couriers have direct access and can skip the appointment backlog.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — typically 2 weeks out", fpc: "No — we handle the Detroit agency submission" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "In-Person Requirement", agency: "Yes — must drive 25 miles to downtown", fpc: "No — ship from Canton" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Canton residents are 25 miles from downtown Detroit but traffic on I-96 can make the drive unpredictable. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-passport-line",
      title: "Using a Passport Card for Air Travel",
      description: "Many Canton-area residents use a passport card or enhanced license for Canada driving trips — but these are not valid for air travel. If you fly internationally, you need a passport book.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo shops in Canton sometimes produce photos that meet ID requirements but not passport standards. Photos must be exactly 2x2 inches with a plain white or off-white background, taken within 6 months.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form for New Passports",
      description: "Missing signatures or incorrect form completion are the #1 rejection reasons at Canton-area acceptance facilities. Our document review catches these before submission.",
    },
    {
      icon: "ri-truck-line",
      title: "Not Using a Courier for Western Suburbs",
      description: "Canton residents who drive themselves to the Detroit Passport Agency spend 1.5+ hours round-trip plus parking and waiting. A courier saves you the entire day and gets faster results.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming the Detroit Agency Has Walk-In Slots",
      description: "The Detroit Passport Agency requires appointments and is typically booked 2 weeks out. There are no same-day walk-in slots — plan ahead or use Fast Passport Center's courier service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Canton or western Wayne County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx on Ford Road or in Plymouth, Northville, or Westland." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier hand-delivers to the Detroit Passport Agency — no appointment wait or downtown drive required." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Canton address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Where is the nearest passport agency for Canton residents?", a: "The nearest regional passport agency is the Detroit Passport Agency at 211 W. Fort St, Suite 1300, Detroit, MI 48226 — approximately 25 miles east of Canton (about 35 minutes via I-96). It is open Monday through Friday 8:00 AM – 3:00 PM by appointment only. Appointments are typically booked 2 weeks out. Fast Passport Center's registered couriers have direct access and can skip the appointment backlog — no need for you to drive downtown." },
    { q: "How fast can I get an emergency passport in Canton?", a: "Fast Passport Center offers emergency passport processing in Canton, Michigan in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Detroit Passport Agency — without you needing to take a day off work or drive downtown." },
    { q: "Can I get a passport at the Canton Post Office?", a: "Yes, the Canton Post Office at 435 S Canton Center Rd offers passport acceptance services Monday through Friday. However, they only process routine and standard expedited applications via mail. For emergency processing within 2 weeks, you will need either an appointment at the Detroit Passport Agency or Fast Passport Center's courier service." },
    { q: "Does Fast Passport Center serve Plymouth and Northville?", a: "Absolutely. Fast Passport Center serves all of western Wayne County including Canton, Plymouth, Northville, Westland, Livonia, Garden City, Inkster, Wayne, and Romulus. Our courier service works the same way everywhere — complete online, ship documents, we handle the rest." },
    { q: "What airports do Canton-area travelers use for international flights?", a: "Detroit Metro Wayne County Airport (DTW) in Romulus is the primary international departure hub for Canton-area travelers — approximately 20 miles southeast via I-96 and I-275. DTW offers direct flights to London, Amsterdam, Tokyo, Paris, Mexico City, Cancun, and dozens of other international destinations. Ann Arbor's airport (ARB) is general aviation only." },
    { q: "How much does an expedited passport cost in Canton, MI?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Can Canton residents get passport photos taken locally?", a: "Yes. Walgreens and CVS locations throughout Canton, Plymouth, and Northville offer passport photos. UPS Stores, FedEx Office locations, and many AAA offices also provide passport photos. Photos must be 2x2 inches on a white or off-white background, taken within the last 6 months." },
    { q: "Do I need to drive to Detroit for my passport?", a: "No. With Fast Passport Center, you never need to drive to Detroit. We handle everything by courier. You complete your application online, ship your documents from any Canton-area UPS or FedEx, and our registered courier takes them to the Detroit Passport Agency for you. Your new passport is shipped back to your address." },
  ],
  photoLocations: [
    { name: "Walgreens — Canton", address: "44541 Ford Rd, Canton, MI 48187", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Canton", address: "42400 Ford Rd, Canton, MI 48187", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Canton", address: "44514 Ford Rd, Canton, MI 48187", hours: "Mon–Fri: 8AM–6:30PM, Sat: 9AM–4PM" },
    { name: "Walgreens — Plymouth", address: "1160 Ann Arbor Rd, Plymouth, MI 48170", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    { name: "Michigan SOS — Canton", address: "7327 N Canton Center Rd, Canton, MI 48187", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Michigan Secretary of State office for driver's licenses, REAL ID, and vehicle registration. Reserve a spot online to avoid wait times.", featured: true },
    { name: "Michigan SOS — Plymouth", address: "44545 Ann Arbor Rd, Plymouth, MI 48170", hours: "Mon–Fri: 9:00AM–5:00PM", note: "Serves Plymouth and northern Canton communities. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "DTW",
      name: "Detroit Metro Wayne County Airport",
      distance: "Approximately 20 miles southeast via I-96 and I-275 — about 30 minutes",
      address: "1 Detroit Metro Airport, Romulus, MI 48242",
      note: "Primary international hub for Canton-area travelers. Delta Air Lines hub. Direct flights to London, Amsterdam, Tokyo, Paris, Mexico City, Cancun, and 40+ international destinations.",
      flightUrl: "https://www.metroairport.com/",
      flightLabel: "DTW Flight Info",
      featured: true,
    },
    {
      code: "ARB",
      name: "Ann Arbor Municipal Airport",
      distance: "Approximately 15 miles southwest of Canton — general aviation only",
      address: "801 Airport Dr, Ann Arbor, MI 48108",
      note: "General aviation airport with no scheduled commercial service. Not used for international travel.",
      flightUrl: "https://www.annarborairport.com/",
      flightLabel: "ARB Airport Info",
      featured: false,
    },
    {
      code: "PTK",
      name: "Oakland County International Airport",
      distance: "Approximately 20 miles north via I-275 — general aviation and some regional",
      address: "6500 Highland Rd, Waterford, MI 48327",
      note: "Primarily general aviation with some regional charter flights. Not used for scheduled international travel.",
      flightUrl: "https://www.oakgov.com/aviation/",
      flightLabel: "PTK Airport Info",
      featured: false,
    },
  ],
};